import { useState } from "react";
import { Lock, ArrowRight, ShieldWarning } from "@phosphor-icons/react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { useAuth } from "../../contexts/AuthContext";
import { setNewPassword } from "../../api/auth";
import Logo from "./Logo";

function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || "Failed to set new password. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  return String(detail);
}

/**
 * ForcedPasswordResetScreen - Mandatory interceptor shown whenever the
 * authenticated user's `password_reset_required` flag is true (e.g. an
 * admin just generated a temporary password for them). Blocks all other
 * access until they set a new permanent password.
 */
export default function ForcedPasswordResetScreen() {
  const { signOut, updateUser } = useAuth();
  const [newPassword, setNewPasswordValue] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const passwordChecks = {
    length: newPassword.length >= 8,
    upper: /[A-Z]/.test(newPassword),
    digit: /[0-9]/.test(newPassword),
    special: /[!@#$%^&*()_+\-=\[\]{}|;':",./<>?`~\\]/.test(newPassword),
  };
  const passwordValid = Object.values(passwordChecks).every(Boolean);
  const passwordsMatch = newPassword.length > 0 && newPassword === confirmPassword;
  const canSubmit = passwordValid && passwordsMatch && !loading;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!passwordValid) {
      setError("Please meet all password requirements below.");
      return;
    }
    if (!passwordsMatch) {
      setError("Passwords do not match.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      await setNewPassword(newPassword);
      // Reflect the cleared flag immediately so the app unblocks without a full reload.
      updateUser({ password_reset_required: false });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen password-bg flex items-center justify-center p-4"
      data-testid="forced-password-reset-screen"
    >
      <div className="absolute inset-0 bg-white/80 backdrop-blur-sm" />

      <div className="relative z-10 w-full max-w-md">
        <div className="bg-white border border-slate-200 p-8 shadow-lg">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center mb-4">
              <Logo size={56} />
            </div>
            <h1 className="font-cabinet text-2xl font-black tracking-tight text-slate-900 mb-2">
              Set a New Password
            </h1>
            <p className="text-sm text-slate-500 mt-2 font-sans flex items-center justify-center gap-1.5">
              <ShieldWarning size={16} className="text-amber-500" />
              You must set a permanent password before continuing.
            </p>
          </div>

          <form onSubmit={onSubmit} className="space-y-3">
            <div className="relative">
              <Lock
                size={18}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <Input
                type="password"
                placeholder="New password"
                value={newPassword}
                onChange={(e) => setNewPasswordValue(e.target.value)}
                className="pl-10 h-12 rounded-none border-slate-300 bg-slate-50 focus-visible:ring-1 focus-visible:ring-primary focus-visible:border-primary font-mono text-sm"
                data-testid="forced-reset-new-password-input"
                autoFocus
                required
              />
            </div>

            <div className="relative">
              <Lock
                size={18}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <Input
                type="password"
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="pl-10 h-12 rounded-none border-slate-300 bg-slate-50 focus-visible:ring-1 focus-visible:ring-primary focus-visible:border-primary font-mono text-sm"
                data-testid="forced-reset-confirm-password-input"
                required
              />
            </div>

            {newPassword.length > 0 && (
              <ul className="text-xs mt-1.5 ml-1 space-y-0.5">
                <li className={passwordChecks.length ? "text-green-600" : "text-red-500"}>
                  {passwordChecks.length ? "✓" : "✗"} Minimum 8 characters
                </li>
                <li className={passwordChecks.upper ? "text-green-600" : "text-red-500"}>
                  {passwordChecks.upper ? "✓" : "✗"} One uppercase letter
                </li>
                <li className={passwordChecks.digit ? "text-green-600" : "text-red-500"}>
                  {passwordChecks.digit ? "✓" : "✗"} One numeric value
                </li>
                <li className={passwordChecks.special ? "text-green-600" : "text-red-500"}>
                  {passwordChecks.special ? "✓" : "✗"} One special character
                </li>
                {confirmPassword.length > 0 && (
                  <li className={passwordsMatch ? "text-green-600" : "text-red-500"}>
                    {passwordsMatch ? "✓" : "✗"} Passwords match
                  </li>
                )}
              </ul>
            )}

            {error && (
              <div
                className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm font-medium"
                data-testid="forced-reset-error"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={!canSubmit}
              className="w-full h-12 rounded-sm bg-primary hover:bg-primary-hover text-white font-semibold tracking-wide uppercase text-xs"
              data-testid="forced-reset-submit-btn"
            >
              {loading ? (
                <div className="spinner" />
              ) : (
                <>
                  Update Password
                  <ArrowRight size={16} className="ml-2" />
                </>
              )}
            </Button>
          </form>

          <div className="mt-6 pt-4 border-t border-slate-200">
            <button
              type="button"
              onClick={signOut}
              data-testid="forced-reset-logout-btn"
              className="w-full text-sm text-slate-500 hover:text-slate-800 text-center font-medium transition-colors"
            >
              Log out instead
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
